// components/QuoteAuthModal.jsx
// Renders inside the CRM as a modal overlay.
// Talks directly to the Quote system's API (REACT_APP_QUOTE_API_URL).
// Stores the resulting token in the CRM's localStorage under "quoteAuthToken"
// so QuoteButton can read it and pass it on subsequent requests.

import React, { useState } from "react";
import {
  X,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  FileText,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

const QUOTE_API_URL =
  process.env.REACT_APP_QUOTE_API_URL || "http://localhost:8000/api";

// ─── Token helpers (CRM-side storage for the Quote token) ────────────────────

export const quoteAuth = {
  getToken: () => localStorage.getItem("quoteAuthToken"),
  setToken: (token) => localStorage.setItem("quoteAuthToken", token),
  getUser: () => {
    const u = localStorage.getItem("quoteAuthUser");
    return u ? JSON.parse(u) : null;
  },
  setUser: (user) =>
    localStorage.setItem("quoteAuthUser", JSON.stringify(user)),
  clear: () => {
    localStorage.removeItem("quoteAuthToken");
    localStorage.removeItem("quoteAuthUser");
  },
  isAuthenticated: () => !!localStorage.getItem("quoteAuthToken"),
};

// ─── Modal Component ─────────────────────────────────────────────────────────

const QuoteAuthModal = ({ onSuccess, onClose }) => {
  const { isDarkMode } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${QUOTE_API_URL}/team/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.message || "Login failed");
      }

      const token = result.data?.token;
      const user = result.data?.user;

      if (!token) throw new Error("No token returned from Quote system");

      // Store in CRM's localStorage so QuoteButton can read it
      quoteAuth.setToken(token);
      if (user) quoteAuth.setUser(user);

      onSuccess(token);
    } catch (err) {
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundColor: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(4px)",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Panel */}
      <div
        className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
        style={{
          background: isDarkMode
            ? "linear-gradient(145deg, #0f172a 0%, #1e293b 100%)"
            : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
          border: isDarkMode
            ? "1px solid rgba(99,102,241,0.25)"
            : "1px solid rgba(99,102,241,0.2)",
        }}
      >
        {/* Decorative top bar */}
        <div
          className="h-1 w-full"
          style={{
            background: "linear-gradient(90deg, #6366f1, #8b5cf6, #06b6d4)",
          }}
        />

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-xl"
              style={{
                background: "rgba(99,102,241,0.15)",
                border: "1px solid rgba(99,102,241,0.3)",
              }}
            >
              <FileText size={18} className="text-indigo-400" />
            </div>
            <div>
              <h2
                className={`font-semibold text-lg leading-tight ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Quote System Login
              </h2>
              <p
                className={`text-xs mt-0.5 ${
                  isDarkMode ? "text-slate-400" : "text-gray-500"
                }`}
              >
                Sign in to your quote account to continue
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className={`transition-colors mt-0.5 ${
              isDarkMode
                ? "text-slate-500 hover:text-slate-300"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <X size={18} />
          </button>
        </div>

        {/* Divider */}
        <div
          className="mx-6 h-px"
          style={{
            background: isDarkMode
              ? "rgba(255,255,255,0.07)"
              : "rgba(0,0,0,0.07)",
          }}
        />

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <div
              className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-sm"
              style={{
                background: isDarkMode
                  ? "rgba(239,68,68,0.1)"
                  : "rgba(239,68,68,0.05)",
                border: isDarkMode
                  ? "1px solid rgba(239,68,68,0.25)"
                  : "1px solid rgba(239,68,68,0.2)",
              }}
            >
              <AlertCircle
                size={15}
                className={`mt-0.5 shrink-0 ${
                  isDarkMode ? "text-red-400" : "text-red-500"
                }`}
              />
              <span className={isDarkMode ? "text-red-300" : "text-red-600"}>
                {error}
              </span>
            </div>
          )}

          {/* Email */}
          <div className="space-y-1.5">
            <label
              className={`block text-xs font-medium uppercase tracking-wider ${
                isDarkMode ? "text-slate-400" : "text-gray-500"
              }`}
            >
              Email
            </label>
            <div className="relative">
              <Mail
                size={15}
                className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                  isDarkMode ? "text-slate-500" : "text-gray-400"
                }`}
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                placeholder="you@example.com"
                className={`w-full pl-9 pr-3 py-2.5 rounded-lg text-sm outline-none focus:ring-2 transition-all ${
                  isDarkMode
                    ? "text-white placeholder-slate-600"
                    : "text-gray-900 placeholder-gray-400"
                }`}
                style={{
                  background: isDarkMode
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.03)",
                  border: isDarkMode
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid rgba(0,0,0,0.1)",
                  "--tw-ring-color": "rgba(99,102,241,0.5)",
                }}
              />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <label
              className={`block text-xs font-medium uppercase tracking-wider ${
                isDarkMode ? "text-slate-400" : "text-gray-500"
              }`}
            >
              Password
            </label>
            <div className="relative">
              <Lock
                size={15}
                className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                  isDarkMode ? "text-slate-500" : "text-gray-400"
                }`}
              />
              <input
                type={showPw ? "text" : "password"}
                required
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                placeholder="Enter your password"
                className={`w-full pl-9 pr-10 py-2.5 rounded-lg text-sm outline-none focus:ring-2 transition-all ${
                  isDarkMode
                    ? "text-white placeholder-slate-600"
                    : "text-gray-900 placeholder-gray-400"
                }`}
                style={{
                  background: isDarkMode
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.03)",
                  border: isDarkMode
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid rgba(0,0,0,0.1)",
                  "--tw-ring-color": "rgba(99,102,241,0.5)",
                }}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                  isDarkMode
                    ? "text-slate-500 hover:text-slate-300"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-1 ${
              isDarkMode ? "text-white" : "text-white"
            }`}
            style={{
              background: loading
                ? "rgba(99,102,241,0.4)"
                : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              boxShadow: loading ? "none" : "0 4px 15px rgba(99,102,241,0.35)",
            }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={15} className="animate-spin" />
                Signing in…
              </span>
            ) : (
              "Sign In to Quote System"
            )}
          </button>
        </form>

        {/* Footer note */}
        <div className="px-6 pb-5">
          <p
            className={`text-center text-xs ${
              isDarkMode ? "text-slate-600" : "text-gray-400"
            }`}
          >
            This is your separate Quote system account — not your CRM login.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuoteAuthModal;
